import React from "react";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/contexts/AuthContext";
import { WsSection } from "./shared";
import { t } from "@/lib/negotiationLabels";
import { translateFr as tfr } from "@/i18n";

const SECTION_IDS = ["profile", "contract", "market_value", "career", "agent_contact", "qr_code"];

export function ContactStep({ draft, update }) {
  const { user } = useAuth();
  const contact = draft.contact || {};
  const qr = contact.qr || { destination: "profile", custom_url: "" };
  const sections = draft.sections || {};

  return (
    <div data-testid="ws-step-contact">
      <WsSection title={t("negotiationProfile.contact.agent")} subtitle={t("negotiationProfile.contact.agentHint")}>
        <div className="rounded-xl border border-border bg-secondary/30 p-3.5 mb-4">
          <p className="text-sm font-semibold" data-testid="contact-agent-name">{user?.name || "—"}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{user?.email || "—"}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="ct-agency" className="text-xs">{t("negotiationProfile.contact.agency")}</Label>
            <Input id="ct-agency" value={contact.agency || ""} onChange={(e) => update(["contact", "agency"], e.target.value)}
              placeholder="—" className="h-10" data-testid="contact-agency-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ct-phone" className="text-xs">{t("negotiationProfile.contact.phone")}</Label>
            <Input id="ct-phone" value={contact.phone || ""} onChange={(e) => update(["contact", "phone"], e.target.value)}
              placeholder="—" className="h-10" data-testid="contact-phone-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ct-city" className="text-xs">{t("negotiationProfile.contact.city")}</Label>
            <Input id="ct-city" value={contact.city || ""} onChange={(e) => update(["contact", "city"], e.target.value)}
              placeholder="—" className="h-10" data-testid="contact-city-input" />
          </div>
        </div>
      </WsSection>

      <WsSection title={t("negotiationProfile.contact.qr")} testid="contact-qr">
        <RadioGroup value={qr.destination} onValueChange={(v) => update(["contact", "qr", "destination"], v)} className="space-y-2">
          {[["profile", t("negotiationProfile.contact.qr.profile")],
            ["custom", t("negotiationProfile.contact.qr.custom")],
            ["none", t("negotiationProfile.contact.qr.none")]].map(([id, label]) => (
            <label key={id} className="flex items-center gap-3 rounded-xl border border-border bg-secondary/20 px-3.5 py-2.5 cursor-pointer hover:border-athlynx/30 transition-colors">
              <RadioGroupItem value={id} data-testid={`qr-dest-${id}`} />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </RadioGroup>
        {qr.destination === "custom" && (
          <div className="mt-3 space-y-1.5 max-w-md">
            <Label htmlFor="qr-url" className="text-xs">{t("negotiationProfile.contact.qr.customUrl")}</Label>
            <Input id="qr-url" value={qr.custom_url || ""} onChange={(e) => update(["contact", "qr", "custom_url"], e.target.value)}
              placeholder="https://…" className="h-10" data-testid="qr-custom-url-input" />
            {qr.custom_url && !/^https?:\/\//.test(qr.custom_url) && (
              <p className="text-xs text-danger" data-testid="qr-url-error">{tfr("URL invalide — elle doit commencer par http:// ou https://")}</p>
            )}
          </div>
        )}
        {qr.destination === "profile" && (
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground mt-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warning" aria-hidden="true" />
            {t("negotiationProfile.contact.qr.warning")}
          </p>
        )}
      </WsSection>

      <WsSection title={t("negotiationProfile.contact.sections")} testid="contact-sections">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {SECTION_IDS.map((sid) => (
            <label key={sid} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/20 px-3.5 py-2.5 cursor-pointer">
              <span className="text-sm">{t(`negotiationProfile.sections.${sid}`)}</span>
              <Switch checked={sections[sid] !== false} onCheckedChange={(v) => update(["sections", sid], v)}
                aria-label={t(`negotiationProfile.sections.${sid}`)} data-testid={`section-toggle-${sid}`} />
            </label>
          ))}
        </div>
      </WsSection>
    </div>
  );
}
